/**
 * Clipboard paste coercion for ArTable cells.
 *
 * Turns parsed clipboard content (structured envelopes or external text lines)
 * into a value the destination column can store. Reference columns keep the
 * entity rules: same entityId+group pastes verbatim, cross-column references
 * downgrade to a manual string, external text becomes a manual string.
 * Field columns coerce per `fieldDataTypeId`; same-type structured envelopes
 * paste verbatim after deep Timestamp revival (JSON serialization flattens
 * Firestore Timestamps to plain objects). Two failure modes are distinct:
 * blank input coerces to `null` (clears the cell, matching spreadsheet paste
 * semantics); non-blank input the column cannot represent — unparseable
 * numbers/dates, `validation`-rejected format text, manual text into a
 * no-manual reference column — coerces to `undefined` (the cell keeps its
 * value and `pasteDownColumn` counts it as skipped). Consumed by
 * `useArTableClipboard` and `ArRowContextMenu`.
 *
 * A row payload (`structuredRows`) reaching `computePasteValues` is degenerate
 * but reachable — the operator copied rows and pasted into a single column. It
 * takes the first column of each row down the target column, which is the
 * reading that loses the least.
 */

import { Timestamp } from 'firebase/firestore';
import { cellClipboardLabel } from './arCellClipboard';
import type { ArCellClipboardEnvelope, ColumnClipboardTarget, ParsedClipboard } from './arCellClipboard';
import { isEntityReference, isManualEntryValue } from '#types/System.Artifacts.types';
import type { ArCellValue, ArManualEntryValue } from '#types/System.Artifacts.types';
import type { FieldType } from '#types/System.types';
import type { TimeRange } from '#types/System.FieldTypes.types';
import { timestampFromDateString } from '@/dates';

type CoerceInput =
  | { kind: 'external'; raw: string }
  | { kind: 'structured'; envelope: ArCellClipboardEnvelope };

const isBlank = (s: string | undefined | null): boolean => s == null || s.trim() === '';

const coerceNumber = (raw: string): number | null => {
  const isAccountingNegative = /^\([\d$,. ]+\)$/.test(raw);
  const stripped = isAccountingNegative ? raw.slice(1, -1) : raw;
  const cleaned = stripped.replace(/[$,]/g, '').trim();
  const num = parseFloat(cleaned);
  if (Number.isNaN(num)) return null;
  return isAccountingNegative ? -num : num;
};

const coerceBoolean = (raw: string): boolean | null => {
  const lower = raw.toLowerCase();
  if (lower === 'true' || lower === 'yes' || lower === '1') return true;
  if (lower === 'false' || lower === 'no' || lower === '0') return false;
  return null;
};

const coerceRange = (raw: string, fieldDataTypeId: string): TimeRange | null => {
  const parts = raw.split(/\s[-–]\s/);
  if (parts.length !== 2) return null;
  const start = timestampFromDateString(parts[0].trim(), fieldDataTypeId);
  const end = timestampFromDateString(parts[1].trim(), fieldDataTypeId);
  return start && end ? { start, end } : null;
};

const isSerializedTimestamp = (record: Record<string, unknown>): boolean => {
  if (typeof record.seconds !== 'number' || typeof record.nanoseconds !== 'number') return false;
  const keys = Object.keys(record);
  if (keys.length === 2) return true;
  return keys.length === 3 && typeof record.type === 'string' && record.type.includes('firestore/timestamp');
};

const reviveTimestamps = (value: unknown): unknown => {
  if (value instanceof Timestamp) return value;
  if (Array.isArray(value)) return value.map(reviveTimestamps);
  if (value != null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (isSerializedTimestamp(record)) {
      return new Timestamp(record.seconds as number, record.nanoseconds as number);
    }
    return Object.fromEntries(Object.entries(record).map(([k, v]) => [k, reviveTimestamps(v)]));
  }
  return value;
};

const coerceToReference = (
  input: CoerceInput,
  target: Extract<ColumnClipboardTarget, { kind: 'reference' }>
): ArCellValue => {
  if (input.kind === 'external') {
    const trimmed = input.raw.trim();
    if (!trimmed) return null;
    if (!target.allowManual) return undefined;
    return { valueType: 'manual', value: trimmed } satisfies ArManualEntryValue;
  }

  const v = reviveTimestamps(input.envelope.value) as ArCellValue;

  if (isEntityReference(v)) {
    const sameColumn =
      input.envelope.entityId === target.entityId &&
      (input.envelope.entityGroupId ?? undefined) === (target.entityGroupId ?? undefined);
    if (sameColumn) return v;
    if (target.allowManual && !isBlank(v.cacheLabel)) {
      return { valueType: 'manual', value: v.cacheLabel } satisfies ArManualEntryValue;
    }
    return undefined;
  }

  if (isManualEntryValue(v)) return target.allowManual ? v : undefined;

  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (!trimmed) return null;
    if (!target.allowManual) return undefined;
    return { valueType: 'manual', value: trimmed } satisfies ArManualEntryValue;
  }

  if (v == null) return null;

  return target.allowManual ? v : undefined;
};

const coerceExternalToField = (raw: string, fieldType: FieldType | undefined): ArCellValue => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const coerced = ((): ArCellValue => {
    switch (fieldType?.fieldDataTypeId) {
      case 'number':
      case 'quantity':
      case 'currency':
      case 'percentage':
        return coerceNumber(trimmed) ?? undefined;
      case 'yesNo':
        return coerceBoolean(trimmed) ?? undefined;
      case 'date':
        return timestampFromDateString(trimmed, 'date') ?? undefined;
      case 'dateTime':
        return timestampFromDateString(trimmed, 'dateTime') ?? undefined;
      case 'time':
        return timestampFromDateString(trimmed, 'time') ?? undefined;
      case 'multiSelect': {
        const items = trimmed.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
        return items.length > 0 ? items : undefined;
      }
      case 'dateRange':
        return coerceRange(trimmed, 'dateRange') ?? undefined;
      case 'timeRange':
        return coerceRange(trimmed, 'timeRange') ?? undefined;
      case 'dateTimeRange':
        return coerceRange(trimmed, 'dateTimeRange') ?? undefined;
      case 'address':
        return { formatted: trimmed, components: { street: trimmed } };
      case 'phone':
        return { formatted: trimmed, components: { number: trimmed } };
      case 'composite':
      case 'sellPriceUI':
      case 'shippingTrackerCompact':
      case 'shippingTrackerExpanded':
        return undefined;
      default:
        return trimmed;
    }
  })();
  if (typeof coerced === 'string' && fieldType?.validation && !fieldType.validation(coerced)) return undefined;
  return coerced;
};

const coerceToField = (
  input: CoerceInput,
  target: Extract<ColumnClipboardTarget, { kind: 'field' }>
): ArCellValue => {
  const fieldType = target.fieldType;
  const fieldDataTypeId = fieldType?.fieldDataTypeId;
  if (input.kind === 'structured') {
    const { envelope } = input;
    if (envelope.value == null) return null;
    if (
      envelope.columnKind === 'field' &&
      fieldDataTypeId != null &&
      envelope.fieldDataTypeId === fieldDataTypeId
    ) {
      return reviveTimestamps(envelope.value) as ArCellValue;
    }
    return coerceExternalToField(cellClipboardLabel(envelope.value), fieldType);
  }
  return coerceExternalToField(input.raw, fieldType);
};

function coerceToColumnValue(input: CoerceInput, target: ColumnClipboardTarget): ArCellValue {
  if (target.kind === 'reference') return coerceToReference(input, target);
  return coerceToField(input, target);
}

function computePasteValues(parsed: ParsedClipboard, target: ColumnClipboardTarget): ArCellValue[] {
  switch (parsed.kind) {
    case 'structured':
      return parsed.envelopes.map((envelope) => coerceToColumnValue({ kind: 'structured', envelope }, target));
    case 'structuredRows':
      return parsed.rows.map((row) => coerceToColumnValue({ kind: 'structured', envelope: row[0] }, target));
    case 'external':
      return parsed.lines.map((raw) => coerceToColumnValue({ kind: 'external', raw }, target));
  }
}

export { coerceToColumnValue, computePasteValues };
export type { CoerceInput };
